"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";

interface HomeRealtimeRefreshProps {
  initialAnnouncementsSignature: string;
  initialContentSignature: string;
}

const INITIAL_REFRESH_DELAY_MS = 15_000;
const REFRESH_INTERVAL_MS = 30_000;
type WindowWithIdleCallback = Window &
  typeof globalThis & {
    requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
  };

function scheduleAfterLoad(callback: () => void) {
  const browserWindow = window as WindowWithIdleCallback;
  const scheduleIdle = () => {
    if (browserWindow.requestIdleCallback) {
      browserWindow.requestIdleCallback(callback, { timeout: 5_000 });
      return;
    }

    browserWindow.setTimeout(callback, INITIAL_REFRESH_DELAY_MS);
  };

  if (document.readyState === "complete") {
    browserWindow.setTimeout(scheduleIdle, INITIAL_REFRESH_DELAY_MS);
    return;
  }

  browserWindow.addEventListener("load", () => browserWindow.setTimeout(scheduleIdle, INITIAL_REFRESH_DELAY_MS), {
    once: true,
  });
}

export function HomeRealtimeRefresh({
  initialAnnouncementsSignature,
  initialContentSignature,
}: HomeRealtimeRefreshProps) {
  const router = useRouter();
  const contentSignatureRef = useRef(initialContentSignature);
  const announcementsSignatureRef = useRef(initialAnnouncementsSignature);

  useEffect(() => {
    let alive = true;
    let intervalId: number | undefined;

    async function refreshRealtimeData() {
      try {
        const [contentResponse, announcementsResponse] = await Promise.all([
          fetch("/api/restaurant-content", { cache: "no-cache" }),
          fetch("/api/announcements?zone=home", { cache: "no-cache" }),
        ]);

        const nextContentSignature = contentResponse.ok
          ? JSON.stringify(await contentResponse.json())
          : contentSignatureRef.current;
        const nextAnnouncementsSignature = announcementsResponse.ok
          ? JSON.stringify(await announcementsResponse.json())
          : announcementsSignatureRef.current;

        if (!alive) return;

        if (
          nextContentSignature !== contentSignatureRef.current ||
          nextAnnouncementsSignature !== announcementsSignatureRef.current
        ) {
          contentSignatureRef.current = nextContentSignature;
          announcementsSignatureRef.current = nextAnnouncementsSignature;
          router.refresh();
        }
      } catch {
        // Keep the server-rendered content if the realtime refresh fails.
      }
    }

    scheduleAfterLoad(() => {
      if (!alive) return;
      void refreshRealtimeData();
      intervalId = window.setInterval(refreshRealtimeData, REFRESH_INTERVAL_MS);
    });

    return () => {
      alive = false;
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [router]);

  return null;
}
